import type { Palette } from '../../_theme/palette.js';
import { hexWithAlpha, shadeColor } from '../../_theme/palette.js';

export interface AnnouncementCta {
  enabled?: boolean;
  label?: string;
  url?: string;
}

export interface AnnouncementThemeColors {
  background?: string;
  text?: string;
  button?: string;
  badge?: string;
}

export interface AnnouncementTheme {
  preset?: string;
  colors?: AnnouncementThemeColors;
  fontFamily?: string;
  shape?: string;
  coverType?: string;
}

export interface AnnouncementShape {
  _id?: string;
  publicId?: string;
  title?: string;
  description?: string;
  body?: unknown;
  bodyHtml?: string;
  bodySummary?: string;
  badge?: string;
  badgeColor?: string;
  coverImage?: { key?: string; url?: string };
  cta?: AnnouncementCta;
  publishedAt?: string;
  theme?: AnnouncementTheme;
  collectionName?: string;
  collectionSlug?: string;
  collectionPublicId?: string;
  collectionType?: string;
  projectName?: string;
  projectSlug?: string;
  projectLogoUrl?: string;
  layout?: string | null;
  layoutSettings?: Record<string, unknown>;
}

interface PresetColors {
  background: string;
  text: string;
  button: string;
  badge: string;
  muted: string;
  dark?: boolean;
}

const PRESET_THEMES: Record<string, PresetColors> = {
  sky: { background: '#f8fafc', text: '#0f172a', button: '#0ea5e9', badge: '#0ea5e9', muted: '#64748b' },
  midnight: { background: '#0b1120', text: '#e2e8f0', button: '#818cf8', badge: '#818cf8', muted: '#94a3b8', dark: true },
  ocean: { background: '#f0fdfa', text: '#0f172a', button: '#06b6d4', badge: '#06b6d4', muted: '#64748b' },
  forest: { background: '#f7fbf7', text: '#0f172a', button: '#10b981', badge: '#10b981', muted: '#64748b' },
  sunset: { background: '#fff7ed', text: '#1c1917', button: '#f97316', badge: '#f97316', muted: '#78716c' },
  slate: { background: '#f8fafc', text: '#0f172a', button: '#0f172a', badge: '#64748b', muted: '#64748b' },
  custom: { background: '#f8fafc', text: '#0f172a', button: '#6366f1', badge: '#6366f1', muted: '#64748b' },
};

const FONT_STACK_PRO =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

// Keyword → stack mapping for the theme editor; raw CSS stacks pass through unchanged.
const FONT_KEYWORD_STACKS: Record<string, string> = {
  system: FONT_STACK_PRO,
  default: FONT_STACK_PRO,
  inter: FONT_STACK_PRO,
  nunito: '"Nunito", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  roboto: '"Roboto", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
};

function resolveFontStack(value: string | undefined): string {
  if (!value) return FONT_STACK_PRO;
  const trimmed = value.trim();
  if (!trimmed) return FONT_STACK_PRO;
  const keyword = FONT_KEYWORD_STACKS[trimmed.toLowerCase()];
  if (keyword) return keyword;
  // Raw CSS stack (has commas or quotes) → passthrough.
  if (trimmed.includes(',') || trimmed.includes('"') || trimmed.includes("'")) return trimmed;
  // Single family name → use as first preference, fall back to Inter.
  return `"${trimmed}", ${FONT_STACK_PRO}`;
}

export function resolveAnnouncementPalette(theme: AnnouncementTheme | undefined): Palette {
  const preset = PRESET_THEMES[theme?.preset ?? 'sky'] ?? PRESET_THEMES.sky!;
  const background = theme?.colors?.background ?? preset.background;
  const text = theme?.colors?.text ?? preset.text;
  const button = theme?.colors?.button ?? preset.button;
  const radius = resolveRadius(theme?.shape);
  const font = resolveFontStack(theme?.fontFamily);
  const dark = preset.dark ?? false;
  const card = dark ? shadeColor(background, 0.05) : '#ffffff';
  const border = dark ? hexWithAlpha('#ffffff', 0.08) : hexWithAlpha('#0f172a', 0.08);

  return {
    primary: button,
    primarySoft: hexWithAlpha(button, 0.12),
    primaryDeep: shadeColor(button, -0.12),
    background,
    card,
    text,
    textMuted: preset.muted,
    onPrimary: '#ffffff',
    border,
    radius: 10,
    radiusLg: radius,
    font,
  };
}

function resolveRadius(shape: string | undefined): number {
  if (shape === 'sharp') return 4;
  if (shape === 'pill') return 32;
  return 16;
}

export function formatPublishedDate(publishedAt: string | undefined): string | null {
  if (!publishedAt) return null;
  return new Date(publishedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Matches the main app's portal: date-fns `formatDistanceToNow` output + " ago".
export function formatRelativeDate(publishedAt: string | undefined): string | null {
  if (!publishedAt) return null;
  const published = new Date(publishedAt).getTime();
  if (!Number.isFinite(published)) return null;
  const diffMs = Date.now() - published;
  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffMinutes < 1) return 'less than a minute ago';
  if (diffMinutes < 2) return '1 minute ago';
  if (diffMinutes < 45) return `${diffMinutes} minutes ago`;
  if (diffMinutes < 90) return 'about 1 hour ago';
  if (diffHours < 24) return `about ${diffHours} hours ago`;
  if (diffHours < 36) return '1 day ago';
  if (diffDays < 26) return `${diffDays} days ago`;
  if (diffDays < 46) return 'about 1 month ago';
  if (diffDays < 320) return `${Math.floor(diffDays / 30)} months ago`;
  if (diffDays < 548) return 'about 1 year ago';
  return `${Math.floor(diffDays / 365)} years ago`;
}
